import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, Lightbulb, Zap } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  BUILDABLE_COUPLES,
  ELECTROCHEM_SOURCES,
  REDUCTION_POTENTIALS,
  SALT_BRIDGE,
  STANDARD_CONDITIONS,
  buildGalvanicCell,
  checkCellPotentialAnswer,
  evaluateCathodePrediction,
  findCouple,
  formatSigned,
  fullEquationText,
  halfEquationText,
  type ReductionCouple,
} from './electrochemicalCells';
import { GALVANIC_CHALLENGES, GALVANIC_QUESTION, calculateGalvanicExplore } from './electrochemicalExplore';
import { ExploreChallenges } from './ExploreChallenges';
import { GalvanicSchematic } from './ElectrochemicalSchematics';
import { WorkedStepsPanel } from './ModelInputs';

const DEFAULT_FIRST = 'zinc';
const DEFAULT_SECOND = 'copper';

function PotentialTable({ selected }: { selected: string[] }) {
  return (
    <div className="overflow-x-auto">
      <table
        className="w-full min-w-[22rem] text-left text-sm"
        aria-label="Standard reduction potentials"
      >
        <thead>
          <tr className="text-xs font-extrabold uppercase tracking-wide text-slate2">
            <th className="py-2 pr-3">Half-equation (reduction)</th>
            <th className="py-2 text-right">E° (V)</th>
          </tr>
        </thead>
        <tbody>
          {REDUCTION_POTENTIALS.map((entry) => (
            <tr
              key={entry.id}
              className={`border-t border-hairline ${selected.includes(entry.id) ? 'bg-wash-sunshine' : ''}`}
            >
              <td className="py-2 pr-3 font-bold text-ink">
                {halfEquationText(entry.reduction)}
                {entry.galvanicUse === 'ranking-only' ? (
                  <span className="ml-2 rounded-full bg-surface px-2 py-0.5 text-[0.65rem] font-extrabold uppercase text-slate2">
                    ranking only
                  </span>
                ) : null}
                {selected.includes(entry.id) ? <span className="sr-only"> (selected)</span> : null}
              </td>
              <td className="py-2 text-right font-extrabold tabular-nums text-ink">
                {formatSigned(entry.e0V)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CoupleSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-sm font-extrabold text-ink">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 w-full rounded-2xl border border-hairline bg-surface px-4 py-3 text-sm font-extrabold text-ink"
      >
        {BUILDABLE_COUPLES.map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entry.label} ({formatSigned(entry.e0V)} V)
          </option>
        ))}
      </select>
    </label>
  );
}

export function GalvanicCellMode() {
  const [firstId, setFirstId] = useState(DEFAULT_FIRST);
  const [secondId, setSecondId] = useState(DEFAULT_SECOND);
  const [prediction, setPrediction] = useState('');
  const [potentialInput, setPotentialInput] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [view, setView] = useState<'explore' | 'practice'>('explore');
  const [lastValidPair, setLastValidPair] = useState<[string, string]>([DEFAULT_FIRST, DEFAULT_SECOND]);
  useScienceTutorSection('GalvanicCellMode', { view, firstId, secondId, prediction, potentialInput, revealed });
  const outcome = useMemo(() => buildGalvanicCell(firstId, secondId), [firstId, secondId]);
  const cell = outcome.status === 'ok' ? outcome : null;
  // Explore keeps the last valid pair while the chosen pair is not supported (fail closed).
  const explore = useMemo(() => calculateGalvanicExplore(...lastValidPair), [lastValidPair]);
  const first = findCouple(firstId) as ReductionCouple;
  const second = findCouple(secondId) as ReductionCouple;

  const resetPrediction = () => {
    setPrediction('');
    setPotentialInput('');
    setRevealed(false);
  };
  const choosePair = (nextFirst: string, nextSecond: string) => {
    setFirstId(nextFirst);
    setSecondId(nextSecond);
    resetPrediction();
    if (buildGalvanicCell(nextFirst, nextSecond).status === 'ok') setLastValidPair([nextFirst, nextSecond]);
  };

  const feedback = cell && revealed && prediction ? evaluateCathodePrediction(cell, prediction) : null;
  const potentialFeedback =
    cell && revealed && potentialInput.trim() !== ''
      ? checkCellPotentialAnswer(cell, Number(potentialInput))
      : null;

  return (
    <section className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
      <aside className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          Standard reduction potentials
        </p>
        <p className="mt-2 text-xs font-bold leading-5 text-slate2">
          {STANDARD_CONDITIONS.label}. Values rounded to 0.01 V from the CRC Handbook. Your course
          data sheet may differ by 0.01–0.02 V; use your data sheet in assessments.
        </p>
        <div className="mt-4">
          <PotentialTable selected={[firstId, secondId]} />
        </div>
        <p className="mt-3 text-[0.7rem] font-medium leading-5 text-slate2">
          Source: {ELECTROCHEM_SOURCES.crc.citation}
        </p>
      </aside>

      <div className="min-w-0 space-y-6">
        <section className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
              Build a galvanic cell
            </p>
            <div className="grid grid-cols-2 rounded-full bg-surface p-1" role="group" aria-label="Galvanic view">
              {(['explore', 'practice'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={view === option}
                  onClick={() => {
                    setView(option);
                    resetPrediction();
                  }}
                  className={`rounded-full px-4 py-1.5 text-sm font-extrabold ${view === option ? 'bg-ink text-white' : 'text-slate2'}`}
                >
                  {option === 'explore' ? 'Explore' : 'Practice'}
                </button>
              ))}
            </div>
          </div>
          <h2 className="mt-2 text-2xl font-extrabold text-ink">
            {view === 'explore' ? 'Pick two half-cells and see E°cell worked out' : 'Which half-cell is reduced?'}
          </h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <CoupleSelect label="Half-cell A (left)" value={firstId} onChange={(value) => choosePair(value, secondId)} />
            <CoupleSelect label="Half-cell B (right)" value={secondId} onChange={(value) => choosePair(firstId, value)} />
          </div>
          {view === 'explore' ? (
            <div className="mt-5">
              <ExploreChallenges
                question={GALVANIC_QUESTION}
                challenges={GALVANIC_CHALLENGES}
                result={outcome.status === 'ok' ? explore : null}
              />
            </div>
          ) : null}

          <div className="mt-5 rounded-[1.75rem] border border-brand-sky/20 bg-surface p-3">
            <GalvanicSchematic first={first} second={second} cell={cell} revealed={view === 'explore' || revealed} />
          </div>

          {outcome.status === 'unsupported' ? (
            <p
              role="status"
              className="mt-5 rounded-2xl border border-brand-coral/30 bg-wash-coral p-4 text-sm font-bold text-ink"
            >
              {outcome.reason}
              {view === 'explore' ? ' The working below still shows the last valid cell.' : ''}
            </p>
          ) : view === 'explore' ? null : (
            <>
              <fieldset className="mt-6">
                <legend className="text-sm font-extrabold text-ink">
                  Predict: which half-cell is the cathode (where reduction happens)?
                </legend>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[first, second].map((entry, index) => (
                    <button
                      key={entry.id}
                      type="button"
                      aria-pressed={prediction === entry.id}
                      onClick={() => {
                        setPrediction(entry.id);
                        setRevealed(false);
                      }}
                      className={`rounded-full border px-5 py-2 text-sm font-extrabold ${prediction === entry.id ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
                    >
                      {index === 0 ? 'A' : 'B'}: {entry.label} is the cathode
                    </button>
                  ))}
                </div>
              </fieldset>
              <label className="mt-5 block max-w-xs text-sm font-extrabold text-ink">
                Optional: calculate E°cell (V)
                <input
                  type="text"
                  inputMode="decimal"
                  value={potentialInput}
                  onChange={(event) => {
                    setPotentialInput(event.target.value);
                    setRevealed(false);
                  }}
                  placeholder="for example 1.10"
                  className="mt-2 w-full rounded-2xl border border-hairline bg-white px-4 py-3 font-bold text-ink outline-none focus:border-brand-sky"
                />
              </label>
              <button
                type="button"
                disabled={!prediction}
                onClick={() => setRevealed(true)}
                className="btn-pill-primary mt-5 inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Zap className="h-4 w-4" aria-hidden="true" /> Connect the cell
              </button>
            </>
          )}

          {cell && feedback ? (
            <div
              role="status"
              className={`mt-6 rounded-[1.75rem] border p-5 ${feedback.correct ? 'border-brand-mint/35 bg-wash-mint' : 'border-brand-coral/30 bg-wash-coral'}`}
            >
              <div className="flex items-start gap-3">
                {feedback.correct ? (
                  <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-brand-mint" aria-hidden="true" />
                ) : (
                  <Lightbulb className="mt-0.5 h-6 w-6 shrink-0 text-brand-coral" aria-hidden="true" />
                )}
                <div className="min-w-0">
                  <h3 className="font-extrabold text-ink">
                    {feedback.correct
                      ? 'Your cathode prediction matches the model.'
                      : `The cathode is ${cell.cathode.label}, not ${cell.anode.label}.`}
                  </h3>
                  <p className="mt-1 text-sm font-medium leading-6 text-slate2">{feedback.message}</p>
                  {potentialFeedback ? (
                    <p className="mt-2 text-sm font-bold leading-6 text-ink">
                      E°cell check: {potentialFeedback.message}
                    </p>
                  ) : null}
                </div>
              </div>
              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                <div className="rounded-2xl bg-white p-4">
                  <dt className="text-xs font-extrabold uppercase tracking-wide text-brand-coral">
                    Anode (−): oxidation
                  </dt>
                  <dd className="mt-1 break-words font-bold text-ink">{halfEquationText(cell.anodeHalf)}</dd>
                </div>
                <div className="rounded-2xl bg-white p-4">
                  <dt className="text-xs font-extrabold uppercase tracking-wide text-brand-sky">
                    Cathode (+): reduction
                  </dt>
                  <dd className="mt-1 break-words font-bold text-ink">{halfEquationText(cell.cathodeHalf)}</dd>
                </div>
                <div className="rounded-2xl bg-white p-4 sm:col-span-2">
                  <dt className="text-xs font-extrabold uppercase tracking-wide text-brand-mint">
                    Overall ({cell.electronsTransferred} electrons cancel)
                  </dt>
                  <dd className="mt-1 break-words font-bold text-ink">{fullEquationText(cell.overall)}</dd>
                </div>
                <div className="rounded-2xl bg-white p-4 sm:col-span-2">
                  <dt className="text-xs font-extrabold uppercase tracking-wide text-ink-soft">
                    Cell potential
                  </dt>
                  <dd className="mt-1 font-bold text-ink">
                    E°cell = E°cathode − E°anode = {formatSigned(cell.cathode.e0V)} − (
                    {formatSigned(cell.anode.e0V)}) = {formatSigned(cell.eCellV)} V.{' '}
                    {cell.spontaneous
                      ? 'Positive, so the reaction is spontaneous as written.'
                      : 'Not positive, so no spontaneous reaction.'}
                  </dd>
                </div>
              </dl>
              <p className="mt-4 text-sm font-medium leading-6 text-slate2">
                Electrons travel through the external wire from the anode to the cathode. In the
                salt bridge, {SALT_BRIDGE.cation} ions move toward the cathode and{' '}
                {SALT_BRIDGE.anion} ions move toward the anode to keep each solution electrically
                neutral.
              </p>
              {[cell.anode, cell.cathode]
                .filter((entry) => entry.note)
                .map((entry) => (
                  <p key={entry.id} className="mt-2 text-xs font-bold leading-5 text-slate2">
                    {entry.label}: {entry.note}
                  </p>
                ))}
            </div>
          ) : null}

          {view === 'explore' ? (
            <div className="mt-6">
              <WorkedStepsPanel
                title="How the model works it out"
                steps={explore.steps}
                stepTestIdPrefix="galvanic-step"
                result={explore.result}
                resultTestId="galvanic-result"
              />
              <p className="mt-3 text-sm font-medium leading-6 text-slate2">
                Electrons travel through the external wire from the anode to the cathode. In the salt bridge,{' '}
                {SALT_BRIDGE.cation} ions move toward the cathode and {SALT_BRIDGE.anion} ions move toward the anode.
              </p>
            </div>
          ) : null}
        </section>

        <section className="rounded-3xl border border-hairline bg-surface p-5 text-sm font-medium leading-6 text-slate2">
          <strong className="text-ink">Model limit:</strong> E° values apply only at standard
          conditions. Real cells give lower voltages because of internal resistance, concentration
          changes and electrode coatings. A positive E°cell says a reaction can happen, not how fast.
        </section>
      </div>
    </section>
  );
}
